import * as React from "react";
import { Button } from "../../ui/button.js";
import { formatMs } from "./TimerPill.js";
import type { ActiveTimer } from "./useTimers.js";

// ---------------------------------------------------------------------------
// FiredAlarmStack
// ---------------------------------------------------------------------------

export interface FiredAlarmStackProps {
  /** Timers whose endsAt has passed (from useTimers fired[]). */
  fired: ActiveTimer[];
  /**
   * Map a stepId → 1-based step number for display.
   * Optional — omit or return null to hide step attribution.
   */
  stepNumberFor?: (stepId: string) => number | null;
  /** Whether to play an audio alert for newly fired timers. */
  soundEnabled: boolean;
  /** Whether to vibrate for newly fired timers. */
  vibrateEnabled: boolean;
  /** Snooze a fired timer by addMs (30_000 / 60_000 / 120_000). */
  onSnooze: (id: string, addMs: number) => void;
  /** Dismiss and stop a fired timer. */
  onStop: (id: string) => void;
}

/**
 * Floating alarm stack for fired timers (spec §6.4.1).
 * Each card is role="alertdialog" (Android heads-up style) over a dimmed
 * backdrop. Cards stack and scroll if there are many.
 *
 * Ring + vibrate on NEWLY fired timers only — tracked via a ref<Set<string>>
 * so re-renders and per-second ticks don't re-alert.
 *
 * rv: custom-snooze input (arbitrary duration) is deferred to v2; only the
 * fixed +30s / +1m / +2m shortcuts are implemented here (spec §6.4.1).
 */
export function FiredAlarmStack({
  fired,
  stepNumberFor,
  soundEnabled,
  vibrateEnabled,
  onSnooze,
  onStop,
}: FiredAlarmStackProps) {
  // Track which fired ids have already triggered the ring/vibrate so we
  // only alert once per newly-fired timer regardless of re-renders/ticks.
  const alertedIds = React.useRef<Set<string>>(new Set());

  const firedIds = fired.map((t) => t.id);

  const firedKey = firedIds.join(",");
  React.useEffect(() => {
    // A timer that left `fired` (snoozed back to running, or stopped) must be able
    // to alert again if it later re-fires — otherwise a snoozed alarm rings only
    // once. Prune ids no longer firing so a re-fire is treated as a new firing.
    for (const id of [...alertedIds.current]) {
      if (!firedIds.includes(id)) alertedIds.current.delete(id);
    }
    for (const timer of fired) {
      if (alertedIds.current.has(timer.id)) continue;

      // Mark alerted immediately so concurrent effect runs don't double-fire.
      alertedIds.current.add(timer.id);

      if (vibrateEnabled && typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate([400, 200, 400]);
      }

      if (soundEnabled) {
        // rv: iOS PWA may suppress audio — vibrate + visual are the reliable
        // channels (spec §6.4.1). Wrap in try/catch for best-effort playback.
        try {
          new Audio("/sounds/alarm.mp3").play().catch(() => {
            // Autoplay may be blocked by browser policy — ignore silently.
          });
        } catch {
          // Audio constructor may not be available — ignore.
        }
      }
    }
  }, [firedKey, fired, vibrateEnabled, soundEnabled]);

  if (fired.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex flex-col items-center justify-center overflow-y-auto py-4"
      aria-label="Fired timer alarms"
    >
      <div className="flex flex-col gap-4 w-full max-w-sm px-4">
        {fired.map((timer) => {
          const overMs = Math.max(0, Date.now() - new Date(timer.endsAt).getTime());
          const stepNum = stepNumberFor ? stepNumberFor(timer.stepId) : null;

          return (
            <div
              key={timer.id}
              role="alertdialog"
              aria-label={`Alarm: ${timer.label}`}
              aria-modal="true"
              className="rounded-2xl border border-border bg-background shadow-2xl px-5 py-4 flex flex-col gap-3"
            >
              {/* Header: label + step */}
              <div>
                <p className="text-lg font-bold text-foreground">{timer.label}</p>
                {stepNum !== null && (
                  <p className="text-sm text-muted-foreground">Step {stepNum}</p>
                )}
                <p className="mt-1 text-base font-mono tabular-nums text-destructive">
                  Over by {formatMs(overMs)}
                </p>
              </div>

              {/* Snooze row */}
              <div className="flex gap-2">
                <Button
                  size="cook"
                  variant="outline"
                  aria-label={`Snooze ${timer.label} by 30 seconds`}
                  onClick={() => onSnooze(timer.id, 30_000)}
                  className="flex-1"
                >
                  +30s
                </Button>
                <Button
                  size="cook"
                  variant="outline"
                  aria-label={`Snooze ${timer.label} by 1 minute`}
                  onClick={() => onSnooze(timer.id, 60_000)}
                  className="flex-1"
                >
                  +1m
                </Button>
                <Button
                  size="cook"
                  variant="outline"
                  aria-label={`Snooze ${timer.label} by 2 minutes`}
                  onClick={() => onSnooze(timer.id, 120_000)}
                  className="flex-1"
                >
                  +2m
                </Button>
              </div>

              {/* Primary dismiss */}
              <Button
                size="cook"
                variant="default"
                aria-label={`Stop & dismiss ${timer.label}`}
                onClick={() => onStop(timer.id)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Stop & dismiss
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
